import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
import { ReactComponent as SearchIcon } from '../../assets/icons/SearchIcon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/EditIcon.svg';
import { AnimatePresence, motion } from "framer-motion";
import ContactDetailsSideBar from './ContactDetailsSideBar'
import CreateContactForm from './CreateContactForm'
import { getAuth } from "firebase/auth";
import ConfirmationModal from './ConfirmationModal'; 


const ContactSideBar = React.forwardRef(({ isVisible, onClose }, ref) => {  const [selectedContact, setSelectedContact] = useState(null);
const [isCreating, setIsCreating] = useState(false);
const [isEditing, setIsEditing] = useState(false);
const [contacts, setContacts] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [isSearching, setIsSearching] = useState(false);
const [filterType, setFilterType] = useState('all');
const [initialLetter, setInitialLetter] = useState('all');
const [contactToEdit, setContactToEdit] = useState(null);
const [isModalVisible, setIsModalVisible] = useState(false);
const [contactToDelete, setContactToDelete] = useState(null);

const fetchContacts = async () => {
setLoading(true);
setError(null);
try {
// --- Proveedores ---
const proveedoresSnapshot = await getDocs(collection(db, "proveedores"));
const proveedoresList = proveedoresSnapshot.docs.map(doc => ({
id: doc.id,
...doc.data(),
type: "proveedor",
displayName: doc.data().razonSocial || "Sin razón social"
}));

// --- Clientes ---
const clientesSnapshot = await getDocs(collection(db, "clientes"));
// Cuentas de staff usadas para probar el sitio público como si fueran
// clientes (Martín Gallo, Tomás Sitta) — no son clientes reales. Sacar
// cuando ya no hagan falta para pruebas.
const UIDS_CUENTAS_DE_PRUEBA = ['31464078', '42876938', '34853820', '36398917'];
const clientesList = clientesSnapshot.docs
.filter(doc => !UIDS_CUENTAS_DE_PRUEBA.includes(doc.id))
.map(doc => ({
id: doc.id,
...doc.data(),
type: "cliente",
displayName:
(doc.data().nombre && doc.data().apellido)
? `${doc.data().nombre} ${doc.data().apellido}`
: doc.data().razonSocial || doc.data().nombre || "Sin nombre/razón social"
}));

// --- Otros ---
const otrosSnapshot = await getDocs(collection(db, "otros"));
const otrosList = otrosSnapshot.docs.map(doc => ({
id: doc.id,
...doc.data(),
type: "otros",
displayName:
(doc.data().nombre && doc.data().apellido)
? `${doc.data().nombre} ${doc.data().apellido}`
: doc.data().nombre || "Sin nombre"
}));

// --- Unir todo ---
const allContacts = [...proveedoresList, ...clientesList, ...otrosList];
setContacts(allContacts);
} catch (err) {
console.error("Error al obtener contactos:", err);
setError("No se pudieron cargar los contactos.");
} finally {
setLoading(false);
}
};


useEffect(() => {
if (isVisible) {
fetchContacts();
}
}, [isVisible]);
useEffect(() => {
if (!isVisible) {
setSelectedContact(null);
setIsCreating(false);
setIsEditing(false);
setSearchTerm('');
setFilterType('all');
setInitialLetter('all');
}
}, [isVisible]);
const handleBackFromForm = () => {
setIsCreating(false);
setIsEditing(false);
setContactToEdit(null);
fetchContacts();
};
const handleEditContact = (contact) => {
setContactToEdit(contact);
setIsEditing(true);
setSelectedContact(null);
};
const handleDeleteContact = (contact) => {
setContactToDelete(contact);
setIsModalVisible(true);
};
const handleConfirmDelete = async () => {
setIsModalVisible(false);
if (!contactToDelete) return;
try {
const url = 'https://backend-288175208699.europe-west1.run.app/delete_contact';
const user = getAuth().currentUser;
const idToken = await user.getIdToken();
const res = await fetch(url, {
method: 'DELETE',
headers: {
'Content-Type': 'application/json',
'Authorization': `Bearer ${idToken}`,
},
body: JSON.stringify({
id: contactToDelete.id,
type: contactToDelete.type,
}),
});
const data = await res.json();
if (!res.ok) {
throw new Error(data.error || 'Error al eliminar el contacto.');
}
alert(data.message);
setSelectedContact(null);
fetchContacts();
} catch (err) {
console.error('Error al eliminar el contacto:', err);
alert(`No se pudo eliminar el contacto: ${err.message}`);
} finally {
setContactToDelete(null);
}
};
const handleCancelDelete = () => {
setIsModalVisible(false);
setContactToDelete(null);
};
const filteredContacts = contacts.filter(contact => {
const mainName = contact.displayName;
const matchesFilterType = filterType === 'all' || contact.type === filterType;
const matchesSearchTerm = !searchTerm || (mainName && mainName.toLowerCase().includes(searchTerm.toLowerCase()));
const matchesInitialLetter = initialLetter === 'all' || (mainName && mainName.toLowerCase().startsWith(initialLetter.toLowerCase()));
return matchesFilterType && matchesSearchTerm && matchesInitialLetter;
});
const groupedContacts = filteredContacts.reduce((acc, contact) => {
if (!contact.displayName) return acc;
const firstLetter = contact.displayName.charAt(0).toUpperCase();
if (!acc[firstLetter]) {
acc[firstLetter] = [];
}
acc[firstLetter].push(contact);
return acc;
}, {});
const sortedLetters = Object.keys(groupedContacts).sort();
const sidebarClass = `general-sidebar ${isVisible ? "visible" : ""}`;
const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split('');
const handleLetterClick = (letter) => {
if (initialLetter === letter.toLowerCase()) {
setInitialLetter('all');
} else {
setInitialLetter(letter.toLowerCase());
}
setSearchTerm('');
};
return (
<div className={sidebarClass} ref={ref} >
<AnimatePresence mode="wait">
{isCreating || isEditing ? (
<CreateContactForm
onBack={handleBackFromForm}
onContactCreated={handleBackFromForm}
onContactUpdated={handleBackFromForm}
contactToEdit={contactToEdit}
/>
) : selectedContact ? (
<ContactDetailsSideBar
contact={selectedContact}
onBack={() => setSelectedContact(null)}
onEdit={handleEditContact}
onDelete={handleDeleteContact}
/>
) : (
<motion.div
key="list"
initial={{ x: "100%" }}
animate={{ x: 0 }}
exit={{ x: "100%" }}
transition={{ duration: 0.15, ease: "easeInOut" }}
>
<div className="sidebar-header">
{!isSearching && (
<h2 className="sidebar-header-title">Contactos</h2>
)}
<div className="sidebar-header-actions">
<div
className="sidebar-icon-container"
onClick={() => setIsSearching((prev) => !prev)}
><SearchIcon className="sidebar-search-icon"/></div>
<input
type="text"
placeholder="Buscar..."
className={`sidebar-search-input ${isSearching ? "expanded" : "collapsed"}`}
value={searchTerm}
onChange={(e) => {
setSearchTerm(e.target.value);
setInitialLetter('all');
}}
/>
<button onClick={onClose} className="sidebar-close-button" >&times;</button>
</div>
</div>
<div className="sidebar-actions">
<div className="add-task-button" style={{marginBottom:'0'}} onClick={() => setIsCreating(true)}> + Crear contacto
</div>
</div>
<div className="sidebar-filter-section">
<p className="contact-count-label">Contactos ({filteredContacts.length})</p>
<div className="filter-buttons">

<button
className={`filter-button ${filterType === 'cliente' ? 'active' : ''}`}
onClick={() => setFilterType(filterType === 'cliente' ? 'all' : 'cliente')}
>
Clientes
</button>
<button
className={`filter-button ${filterType === 'proveedor' ? 'active' : ''}`}
onClick={() => setFilterType(filterType === 'proveedor' ? 'all' : 'proveedor')}
>
Proveedores
</button>
<button
className={`filter-button ${filterType === 'otros' ? 'active' : ''}`}
onClick={() => setFilterType(filterType === 'otros' ? 'all' : 'otros')}
>
Otros
</button>
</div>
<div className="alphabet-filter">
{alphabet.map(letter => (
<span
key={letter}
className={`letter-button ${initialLetter === letter.toLowerCase() ? 'active' : ''}`}
onClick={() => handleLetterClick(letter)}
>
{letter}
</span>
))}
</div>
</div>
<div className="sidebar-contact-list">
{loading && <p className="sidebar-loading">Cargando contactos...</p>}
{error && <p className="sidebar-error">{error}</p>}
{!loading && !error && filteredContacts.length === 0 && (
<p className="sidebar-no-results">No se encontraron contactos.</p>
)}
{!loading && !error && filteredContacts.length > 0 && (
<ul className="sidebar-contact-ul">
{sortedLetters.map(letter => (
<li key={letter} className="contact-group">
<div className="contact-group-header">
<span className="group-letter">{letter}</span>
<ul className="contact-list-by-letter">
{groupedContacts[letter].map(contact => (
<li key={contact.id} onClick={() => setSelectedContact(contact)} className="sidebar-contact-item">
<p className="sidebar-contact-text" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
<button
onClick={(e) => { e.stopPropagation(); handleEditContact(contact); }}
title="Editar contacto"
style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
>
<EditIcon style={{ width: '14px', height: '14px' }} />
</button>
<strong>{contact.displayName}</strong>
</p>
</li>
))}
</ul>
</div>
</li>
))}
</ul>
)}
</div>
</motion.div>
)}
</AnimatePresence>
<ConfirmationModal
isVisible={isModalVisible}
message={`"${contactToDelete?.displayName}" ha sido eliminado.`}
onConfirm={handleConfirmDelete}
onCancel={handleCancelDelete}
/>
</div>
);
});

export default ContactSideBar;